import { useMemo } from 'react'

import { ChartData, ChartOptions } from 'chart.js'
import { Chart } from 'react-chartjs-2'

import {
  COLOR_LAST_BID,
  COLOR_LAST_COMPLETED_JOB,
  SUPPLIER_ACTIVITY_RECENCY_CHART_HEIGHT,
} from 'components/reports/Metrics/constants/SupplierActivityRecency'
import { SupplierActivityMeanSeriesPointDto } from 'generated/graphql'

import { LINE_STYLE } from '../components/chartConfig'

interface Props {
  points: SupplierActivityMeanSeriesPointDto[]
  height?: number
}

export const MeanDailyValuesChart = ({
  points,
  height = SUPPLIER_ACTIVITY_RECENCY_CHART_HEIGHT,
}: Props) => {
  const data = useMemo<ChartData<'line', (number | null)[], string>>(
    () => ({
      labels: points.map((point) =>
        point.daysBeforeAnalysisDate === 0 ? 'Today (0)' : `${point.daysBeforeAnalysisDate}`,
      ),
      datasets: [
        {
          ...LINE_STYLE,
          type: 'line' as const,
          label: 'Mean days since last bid',
          data: points.map((point) => point.meanDaysSinceLastBid ?? null),
          borderColor: COLOR_LAST_BID,
          backgroundColor: COLOR_LAST_BID,
          spanGaps: false,
        },
        {
          ...LINE_STYLE,
          type: 'line' as const,
          label: 'Mean days since last completed job',
          data: points.map((point) => point.meanDaysSinceLastCompletedJob ?? null),
          borderColor: COLOR_LAST_COMPLETED_JOB,
          backgroundColor: COLOR_LAST_COMPLETED_JOB,
          spanGaps: false,
        },
      ],
    }),
    [points],
  )

  const options = useMemo<ChartOptions<'line'>>(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: 'index', intersect: false },
      scales: {
        x: {
          grid: { display: false },
          title: { display: true, text: 'Days before today' },
          ticks: { maxRotation: 0, autoSkip: true, maxTicksLimit: 10 },
        },
        y: {
          beginAtZero: true,
          title: { display: true, text: 'Mean days' },
        },
      },
      plugins: {
        legend: { position: 'top', align: 'end', labels: { usePointStyle: true, boxWidth: 12 } },
        tooltip: {
          callbacks: {
            label: (context) => {
              const point = points[context.dataIndex]
              const isBid = context.datasetIndex === 0
              const value = isBid ? point.meanDaysSinceLastBid : point.meanDaysSinceLastCompletedJob
              const count = isBid ? point.bidSupplierCount : point.jobSupplierCount

              if (value == null) {
                return `${context.dataset.label}: no suppliers in this band`
              }

              return `${
                context.dataset.label
              }: ${value} days (${count.toLocaleString()} supplier(s))`
            },
          },
        },
      },
    }),
    [points],
  )

  return (
    <div style={{ height, position: 'relative' }}>
      <Chart<'line', (number | null)[], string> type='line' data={data} options={options} />
    </div>
  )
}
